import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Mail, Lock, AlertCircle } from 'lucide-react';
import { cn } from '../lib/utils';
import toast from 'react-hot-toast';

export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError('');

      // First, authenticate the user
      const authResponse = await fetch('https://x1lc-aku3-stjs.p7.xano.io/api:O20NksVB/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      if (!authResponse.ok) {
        throw new Error('Email ou mot de passe incorrect');
      }

      const { authToken } = await authResponse.json();

      if (!authToken) {
        throw new Error('Token not found in response');
      }

      // Then, fetch the user profile
      const profileResponse = await fetch('https://x1lc-aku3-stjs.p7.xano.io/api:O20NksVB/auth/me', {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (!profileResponse.ok) {
        throw new Error('Failed to fetch user profile');
      }

      const profileData = await profileResponse.json();

      // Try to fetch business profile first
      const businessResponse = await fetch('https://x1lc-aku3-stjs.p7.xano.io/api:moW4brDB/business', {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      let businessId;
      if (businessResponse.ok) {
        const businesses = await businessResponse.json();
        const businessProfile = businesses.find((b: any) => b.email === email);
        if (businessProfile) {
          businessId = businessProfile.id;
        }
      }

      // If not a business user, try to fetch worker profile
      let workerId;
      if (!businessId) {
        const workerResponse = await fetch('https://x1lc-aku3-stjs.p7.xano.io/api:moW4brDB/worker', {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        });

        if (workerResponse.ok) {
          const workers = await workerResponse.json();
          const workerProfile = workers.find((w: any) => w.email === email);
          if (workerProfile) {
            workerId = workerProfile.id;
          }
        }
      }

      // Store user data including both business_id and worker_id
      login(authToken, {
        id: profileData.id,
        email: profileData.email,
        name: profileData.name || email.split('@')[0],
        business_id: businessId,
        worker_id: workerId
      });

      toast.success('Connexion réussie');

      // Navigate based on user type
      if (businessId) {
        navigate('/dashboard');
      } else if (workerId) {
        navigate('/profile');
      } else {
        navigate('/register');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Une erreur est survenue';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-gray-50">
      <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Connexion
          </h1>
          <p className="mt-2 text-lg text-gray-600">
            Accédez à votre espace personnel
          </p>
        </div>

        <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-xl border border-gray-100">
          <div className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <div className="flex items-center gap-3 text-red-700">
                    <AlertCircle className="h-5 w-5 flex-shrink-0" />
                    <p className="text-sm font-medium">{error}</p>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400">
                    <Mail className="h-5 w-5" />
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={cn(
                      "block w-full pl-10 pr-4 py-3 rounded-xl border transition-all duration-200",
                      "bg-white/50 backdrop-blur-sm",
                      "border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20",
                      "placeholder-gray-400",
                      "text-gray-900"
                    )}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Mot de passe
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400">
                    <Lock className="h-5 w-5" />
                  </div>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={cn(
                      "block w-full pl-10 pr-4 py-3 rounded-xl border transition-all duration-200",
                      "bg-white/50 backdrop-blur-sm",
                      "border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20",
                      "placeholder-gray-400",
                      "text-gray-900"
                    )}
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className={cn(
                  "w-full py-3 px-4",
                  "bg-teal-600 text-white",
                  "hover:bg-teal-700",
                  "rounded-xl",
                  "text-sm font-medium",
                  "shadow-lg shadow-teal-100",
                  "transform transition-all duration-200",
                  "hover:scale-105",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                  "flex items-center justify-center"
                )}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2" />
                    Connexion...
                  </>
                ) : (
                  'Se connecter'
                )}
              </button>
            </form>

            <div className="mt-8 pt-6 border-t border-gray-200 text-center">
              <p className="text-sm text-gray-600">
                Pas encore de compte ?{' '}
                <Link
                  to="/register"
                  className="font-medium text-teal-600 hover:text-teal-500"
                >
                  Créer un compte
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}